"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowUpLeft, Clock, Eye } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";

import { useApi } from "@/src/api/useApi";
import { getUrlImage, PersianNum, getTimeAgo } from "@/src/tools";

import "swiper/css";

type ArticleRow = {
  id: number;
  article_title: string;
  article_url: string;
  article_image: string;
  article_image_alt?: string | null;
  article_view?: number | null;
  article_time_read?: number | null;
  article_description?: string | null;
  createdAt?: string | null;
  published_at?: string | null;
};

type ArticleListResponse = {
  data?: {
    rows?: ArticleRow[];
    count?: number;
  };
};

interface Props {
  currentArticleId: number;
  title?: string;
  size?: number;
}

const RelatedArticles: React.FC<Props> = ({
  currentArticleId,
  title = "مقالات مشابه",
  size = 8,
}) => {
  const { data, isLoading } = useApi<ArticleListResponse>(
    `/article-list?status=1&size=${size}&sort=createdAt&order=DESC`,
    { revalidateOnFocus: false },
  );

  const rows =
    data?.data?.rows?.filter((r) => r.id !== currentArticleId) ?? [];

  if (isLoading || rows.length === 0) return null;

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
          <span className="block w-1 h-5 rounded-full bg-blue-500"></span>
          {title}
        </h2>
        <Link
          href="/blog"
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
        >
          مشاهده همه
          <ArrowUpLeft className="w-3.5 h-3.5" />
        </Link>
      </div>

      <Swiper
        spaceBetween={12}
        slidesPerView={1.2}
        breakpoints={{
          480: { slidesPerView: 1.6 },
          640: { slidesPerView: 2.2 },
          900: { slidesPerView: 3 },
          1100: { slidesPerView: 4 },
        }}
        dir="rtl"
      >
        {rows.map((a) => (
          <SwiperSlide key={a.id}>
            <Link
              href={`/blog/${a.article_url}`}
              className="block group rounded-2xl overflow-hidden border border-gray-100 bg-white hover:shadow-md transition-shadow"
            >
              <div className="relative aspect-[16/10] bg-gray-100 overflow-hidden">
                <Image
                  src={getUrlImage(a.article_image)}
                  alt={a.article_image_alt || a.article_title}
                  width={600}
                  height={375}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-3">
                <h3 className="text-[13px] font-bold text-gray-800 line-clamp-2 leading-6">
                  {a.article_title}
                </h3>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-400">
                  {a.article_view != null && (
                    <span className="inline-flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {PersianNum(a.article_view)}
                    </span>
                  )}
                  {(a.published_at || a.createdAt) && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {getTimeAgo(a.published_at || a.createdAt)}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default RelatedArticles;
